import { useHistory } from 'react-router-dom'
import { authService } from 'modules/auth/auth.service'
import { buildPath } from 'utils/url'
import { extractStringsFromObject } from 'utils/string'
import { toast } from 'components/toast'
import { trace } from 'utils/trace'
import {
  LOCALSTORAGE_TOKEN_NAME,
  DOMAINS,
  COOKIE_INTEGRATION_NAME,
  COOKIE_INTEGRATION_BW_CENTRAL
} from 'src/modules/auth/auth.constants'
import { APP_ROUTES, PLATFORM_ROUTE_GROUPS } from 'src/app/routes/routes.constants'
import { useDispatch } from 'react-redux'
import { getContext } from 'modules/context'
import Cookies from 'js-cookie'
import { getUser, userSlice } from 'src/modules/user'
import { useSelector } from 'store/redux.store'
import { DataLayer, EVENT, GoogleAnalyticsDataLayer } from 'src/utils/google-analytics'
import { MessageTypes } from './use-message.constants'
import { IEventData, IRedirectData } from './use-message.interface'

export const useMessage = (): any => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { user } = useSelector(state => state)

  const alertHandler = ({ body: { type, content } }) => {
    toast({
      message: content,
      type
    })
  }

  const interactionEventHandler = ({ body }: IEventData) => {
    if (body) {
      const { eventCategory, eventAction, eventLabel } = body

      const dataLayer = new DataLayer(EVENT.INTERACTION, eventCategory, eventAction, eventLabel)

      GoogleAnalyticsDataLayer.sendPageInteraction(dataLayer)
    }
  }

  const redirectHandler = ({ body: { route, externalLink = false, externalLinkBlank = false } }: IRedirectData) => {
    const { name, params, queryParams } = route
    const path = buildPath(name, params, queryParams)
    const currentRouteIsVlcSupported = PLATFORM_ROUTE_GROUPS.some(path =>
      path.includes(`/${window.location.pathname.split('/').filter(Boolean)[0]}`)
    )

    if (!currentRouteIsVlcSupported && queryParams?.from === 'vlc' && name === APP_ROUTES.notFound) {
      return
    }

    if (externalLink) {
      window.open(name, externalLinkBlank ? '_blank' : '_self')
      return
    }

    history.push(path)
  }

  const logoutHandler = data => {
    trace.trhowMsg(`
      [trace][6] ,
      from: ${data.from},
      appName: ${data.appName ? data.appName : null}
    `)
    authService.logout(false, user?.profile?.adminId)
  }

  const logoutByExpiredToken = data => {
    trace.trhowMsg(`[trace][8] , appName: ${data.appName ? data.appName : null}`)
    authService.logout(false, user?.profile?.adminId)
  }

  const windowClickHandler = () => {
    window.dispatchEvent(new Event('click'))
  }

  const pathUpdateHandler = ({ message }) => {
    const routesApp = extractStringsFromObject(APP_ROUTES)

    if (routesApp.some((route: String) => route.includes(message))) {
      history.push(message)
      return
    }

    if (message !== location.pathname + location.search && typeof window.history.pushState !== 'undefined') {
      window.history.pushState({}, 'Hotmart', message)
      const pushStateEvent = new CustomEvent('pushState', { detail: { message } })

      dispatchEvent(pushStateEvent)
    }
  }

  const langUpdateHandler = () => location.reload()
  const renewToken =
    KEY_NAME =>
    ({ content }) => {
      const isLogoutCentral = KEY_NAME === 'token'

      localStorage.setItem(LOCALSTORAGE_TOKEN_NAME, content[KEY_NAME])
      Cookies.set(isLogoutCentral ? COOKIE_INTEGRATION_NAME : COOKIE_INTEGRATION_BW_CENTRAL, content[KEY_NAME], {
        domain: process.env.NODE_ENV === 'production' ? DOMAINS.production : DOMAINS.development
      })
      dispatch(getContext())
      dispatch(getUser())
    }

  const activeExtensionsHandler = () => {
    const profile = {
      ...user.profile,
      extensionsFreemium: true
    }

    dispatch(userSlice.actions.updateUser({ profile }))
  }

  const openProductFruitsTourHandler = ({ body: { tourID } }) => {
    window.productFruitsIsReady && window.productFruits?.api.tours.tryStartTour(tourID)
  }

  const handlers: Record<string, Function> = {
    [MessageTypes.ALERT]: alertHandler,
    [MessageTypes.INTERACTION_EVENT]: interactionEventHandler,
    [MessageTypes.REDIRECT]: redirectHandler,
    [MessageTypes.LOGOUT]: logoutHandler,
    [MessageTypes.EXPIRED_TOKEN]: logoutByExpiredToken,
    [MessageTypes.VLC_PATH_UPDATE]: pathUpdateHandler,
    [MessageTypes.VLC_LANG_UPDATE]: langUpdateHandler,
    [MessageTypes.WINDOW_CLICK_LISTENER]: windowClickHandler,
    'ATTENDANT.LOGOUT.SUCCESS': data => {
      renewToken('token')(data)
      history.go('/dashboard')
    },
    'ATTENDANT.LOGIN.SUCCESS': renewToken('access_token'),
    [MessageTypes.ACTIVE_EXTENSIONS]: activeExtensionsHandler,
    [MessageTypes.OPEN_PF_TOUR]: openProductFruitsTourHandler
  }

  const messageHandler = ({ data = {} }: MessageEvent) => {
    const { type: messageType } = data

    if (messageType in handlers) {
      const handler = handlers[messageType]

      handler(data)
    }
  }

  const initMessageListener = () => {
    window.addEventListener('message', messageHandler, false)
  }

  return { initMessageListener }
}
